import React from 'react';
import SalvPage from '../../common/SalvPage';
import { useLocation, useParams } from 'react-router-dom';
import { IconColor, SalvButton, SalvLabel, SalvTooltipWrapper, Size } from '@salvapp/ui-components';
import '../../scenario/MonitoringScenario.scss';
import FieldEditor from '../../scenario/FieldEditor';
import { InternalApiScenarioStatus } from '../../api-internal';
import { useMe } from '../../organisation/useMe';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../../organisation/feature/helper';
import { CAN_MODIFY_RISK_RULE } from '../../auth/permissions';
import { useQueryRiskRule, useUpdateRiskRule } from './riskRuleQueryActions';
import RiskRuleVersions from './RiskRuleVersions';
import { showConfirmationModal } from '../../confirmation-modal/confirmationModalReducer';
import { useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import { faLoader } from '@fortawesome/pro-regular-svg-icons';

export const statusSwitchers = {
  [InternalApiScenarioStatus.ACTIVE]: {
    message: 'This risk rule is live.',
    label: <SalvLabel variant='success'>Active</SalvLabel>,
    name: 'Active',
    action: 'Deactivate',
    confirmationQuestion: 'Are you sure you want to deactivate the risk rule?',
    confirmationDetails:
      'This risk rule will stop generating risks, which might have serious impact on your compliance operations.',
    confirmationText: 'Yes, deactivate',
    nextStatus: InternalApiScenarioStatus.INACTIVE,
  },
  [InternalApiScenarioStatus.INACTIVE]: {
    message: 'This risk rule is not live.',
    label: <SalvLabel variant='danger'>Inactive</SalvLabel>,
  },
  [InternalApiScenarioStatus.ARCHIVED]: {
    message: 'This risk rule is archived and no longer in use.',
    label: <SalvLabel variant='secondary'>Archived</SalvLabel>,
  },
};

const RiskRule = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { handle } = useParams() as { handle: string };
  const { permissions, features } = useMe();

  const { data: riskRule } = useQueryRiskRule(handle);

  const { mutate: updateRiskRule } = useUpdateRiskRule();

  const canModifyRiskRule = permissions.includes(CAN_MODIFY_RISK_RULE);

  return (
    riskRule && (
      <SalvPage
        title={riskRule?.name}
        breadcrumb={{
          title: 'Risk assessment rules',
          to: { pathname: '/risk', search: location.state?.search },
        }}
      >
        <div className='scenario-status-section'>
          <div className='d-flex align-items-center'>
            <div className='text-strong-m m-r-8'>Status</div>
            {statusSwitchers[riskRule.status].label}
            {isFeatureEnabled(ORGANISATION_FEATURE.RISK_CALCULATION_STATUS, features) && (
              <SalvTooltipWrapper
                tooltip={
                  riskRule.calculationInProgress ? 'Rule re-scoring is in progress' : 'Rule scoring is up-to-date'
                }
              >
                <FontAwesomeIcon
                  icon={riskRule.calculationInProgress ? faLoader : faCircleCheck}
                  style={{ color: 'var(--text-medium)', marginLeft: '4px' }}
                  spin={riskRule.calculationInProgress}
                />
              </SalvTooltipWrapper>
            )}
          </div>
          <div className='d-flex align-items-center'>
            <div className='text-secondary m-t-4'>{statusSwitchers[riskRule.status].message}</div>
            {riskRule.status === InternalApiScenarioStatus.ACTIVE && (
              <SalvButton
                variant='secondary'
                size={Size.EXTRA_SMALL}
                label={statusSwitchers[InternalApiScenarioStatus.ACTIVE].action}
                className='m-l-8'
                onClick={() =>
                  dispatch(
                    showConfirmationModal({
                      title: statusSwitchers[InternalApiScenarioStatus.ACTIVE].confirmationQuestion,
                      description: statusSwitchers[InternalApiScenarioStatus.ACTIVE].confirmationDetails,
                      onConfirm: () => {
                        updateRiskRule({
                          handle: riskRule.id!,
                          update: {
                            status: statusSwitchers[InternalApiScenarioStatus.ACTIVE].nextStatus,
                          },
                        });
                      },
                      confirmButtonVariant: 'danger',
                      iconColor: IconColor.DANGER,
                      confirmationText: statusSwitchers[InternalApiScenarioStatus.ACTIVE].confirmationText,
                      cancelText: 'No',
                    })
                  )
                }
                disabled={!canModifyRiskRule}
              />
            )}
          </div>
        </div>

        <div className='scenario-details-section'>
          <div className='details-column-with-scenario-name'>
            <FieldEditor
              label='Risk rule name'
              value={riskRule.name}
              fieldWidth={480}
              onConfirm={(value) => {
                updateRiskRule({
                  handle: riskRule.id!,
                  update: {
                    name: value,
                  },
                });
              }}
              disabled={!canModifyRiskRule}
            />
          </div>
        </div>

        <div className='m-t-24'>
          <RiskRuleVersions riskRule={riskRule} />
        </div>
      </SalvPage>
    )
  );
};

export default RiskRule;
